import {
  Assumption,
  Requirement,
  AssumptionSetAggregate,
} from '@/services/db/models/AssumptionSetAggregate'
import { VariableStateSelectEntry } from '@/services/db/schema'
import { Granularity } from '@/types/variable'
import {
  Connection,
  Interval,
  ParameterizationType,
  TransitionFunction,
  Variable,
} from '@/types/variable'

/**
 * A transformed variable optimized for comparison between two networks.
 * Converts IDs to human-readable names for easier diff display.
 */
export type VariableToCompare = {
  name: string
  type: Variable['type']
  copyReferenceId: string
  intervalUnit: string
  intervals: Interval[] | undefined
  stateNames: string[]
  /**
   * The variable's OWN transition configs, or `null`/`undefined` when it
   * inherits the network default (KBE-758). Kept unresolved on purpose —
   * see `getChangedVariableFields`.
   */
  transitionFunctions: TransitionFunction[] | null | undefined
  optimalStateName: string | undefined
  probabilities: number[]
  granularity: Granularity
  comment: string
  inboundArrows: Array<{
    name: string
    comment: string
    stateNames: string[]
    parentId: string
  }>
  parameterizationType: ParameterizationType
  tags: string[] | null | undefined
  definition: string | null | undefined
  references: string | null | undefined
  formula: string | null | undefined
  prevalenceExplanation: string | null | undefined
}

export const pairOldAndNewVariables = (networks: {
  oldVariables: Array<Variable>
  oldVariableStates: Array<VariableStateSelectEntry>
  newVariables: Array<Variable>
  newVariableStates: Array<VariableStateSelectEntry>
}) => {
  const mappedOldVariables = networks.oldVariables.map((oldVariable) => {
    const arrowsForOldVariable = mapArrowsWithInboundVariableName({
      inboundArrows: oldVariable.inboundArrows,
      variables: networks.oldVariables,
      variableStates: networks.oldVariableStates,
    })
    return mapVariables({
      variable: oldVariable,
      inboundArrows: arrowsForOldVariable,
      variableStates: networks.oldVariableStates,
    })
  })
  const mappedNewVariables = networks.newVariables.map((newVariable) => {
    const arrowsForNewVariable = mapArrowsWithInboundVariableName({
      inboundArrows: newVariable.inboundArrows,
      variables: networks.newVariables,
      variableStates: networks.newVariableStates,
    })
    return mapVariables({
      variable: newVariable,
      inboundArrows: arrowsForNewVariable,
      variableStates: networks.newVariableStates,
    })
  })
  const pairedVariables = pairNewAndOldEntities({
    oldEntities: mappedOldVariables,
    newEntities: mappedNewVariables,
    findEntityInListOfEntity: findVariableInArray,
  })

  return pairedVariables.map(({ newEntity, oldEntity }) => ({
    oldVariable: oldEntity,
    newVariable: newEntity,
  }))
}

const mapArrowsWithInboundVariableName = ({
  inboundArrows,
  variables,
  variableStates,
}: {
  inboundArrows: Array<Connection> | undefined
  variables: Array<Variable>
  variableStates: Array<VariableStateSelectEntry>
}) =>
  inboundArrows?.map((arrow) => {
    const parent = variables.find((n) => n.id === arrow.variableId)

    if (!parent)
      throw new Error('Variable not found for arrow (useNetworksForCompare)')
    return {
      name: parent.name,
      stateNames: parent.stateIds.map((stateId) => {
        const stateWithMetadata = variableStates.find(
          (state) => state.id === stateId,
        )
        if (!stateWithMetadata) {
          throw new Error(
            `State not found for variable: ${parent.name} (stateId: ${stateId})`,
          )
        }
        return stateWithMetadata.name
      }),
      comment: arrow.comment,
      parentId: parent.id,
    }
  }) ?? []

const mapVariables = ({
  variable,
  inboundArrows,
  variableStates,
}: {
  variable: Variable
  inboundArrows: {
    name: string
    comment: string
    stateNames: string[]
    parentId: string
  }[]
  variableStates: Array<VariableStateSelectEntry>
}): VariableToCompare => {
  const stateNames = variable.stateIds.map((stateId) => {
    const stateWithMetadata = variableStates.find(
      (state) => state.id === stateId,
    )
    if (!stateWithMetadata) {
      throw new Error(
        `State not found for variable: ${variable.name} (stateId: ${stateId})`,
      )
    }
    return stateWithMetadata.name
  })
  return {
    name: variable.name,
    type: variable.type,
    copyReferenceId: variable.copyReferenceId,
    intervalUnit: variable.intervalUnit ?? '',
    intervals: variable.intervals,
    stateNames,
    transitionFunctions: variable.transitionFunctions,
    optimalStateName: variableStates.find(
      (state) => state.id === variable.optimalStateId,
    )?.name,
    probabilities: variable.probabilities,
    granularity: variable.intervals
      ? Granularity.SemiContinuous
      : Granularity.Discrete,
    comment: variable.comment,
    inboundArrows,
    parameterizationType: variable.parameterizationType,
    tags: variable.tags,
    definition: variable.definition,
    references: variable.references,
    formula: variable.formula,
    prevalenceExplanation: variable.prevalenceExplanation,
  }
}

function findVariableInArray<
  T extends { copyReferenceId: string; name: string },
>(variables: Array<T>, variable: T) {
  return variables.find((oldVariable) => {
    if (oldVariable.copyReferenceId === variable.copyReferenceId) {
      return true
    }
    return oldVariable.name === variable.name
  })
}

export function pairOldAndNewRequirements({
  oldRequirements,
  newRequirements,
  oldVariables,
  newVariables,
  oldVariableStates,
  newVariableStates,
}: {
  oldRequirements: Array<Requirement>
  newRequirements: Array<Requirement>
  oldVariables: Array<Variable>
  newVariables: Array<Variable>
  oldVariableStates: Array<VariableStateSelectEntry>
  newVariableStates: Array<VariableStateSelectEntry>
}) {
  const oldRequirementsWithCopyReferenceId = addVariablePropsToRequirement(
    oldRequirements,
    oldVariables,
    oldVariableStates,
  )
  const newRequirementsWithCopyReferenceId = addVariablePropsToRequirement(
    newRequirements,
    newVariables,
    newVariableStates,
  )
  const pairedRequirements = pairNewAndOldEntities({
    oldEntities: oldRequirementsWithCopyReferenceId,
    newEntities: newRequirementsWithCopyReferenceId,
    findEntityInListOfEntity: findRequirementInRequirements,
  })
  return pairedRequirements.map(({ newEntity, oldEntity }) => ({
    oldRequirement: oldEntity,
    newRequirement: newEntity,
  }))
}

function findRequirementInRequirements(
  requirements: Array<Requirement & { copyReferenceId: string }>,
  requirement: Requirement & { copyReferenceId: string },
) {
  return requirements.find((oldRequirement) => {
    if (oldRequirement.stateId === requirement.stateId) {
      if (oldRequirement.copyReferenceId === requirement.copyReferenceId) {
        return true
      }
      return oldRequirement.variableName === requirement.variableName
    }
    return false
  })
}

function addVariablePropsToRequirement(
  requirements: Array<Requirement>,
  variables: Array<Variable>,
  variableStates: Array<VariableStateSelectEntry>,
) {
  return requirements.map((requirement) => {
    const variable = variables.find((v) => v.id === requirement.variableId)
    if (!variable) throw new Error('Variable not found for requirement')
    const stateName = variableStates.find(
      (state) => state.id === requirement.stateId,
    )?.name
    if (!stateName && requirement.stateId) {
      throw new Error(
        `State not found for requirement: ${requirement.stateId} (variableName: ${variable.name})`,
      )
    }
    return {
      ...requirement,
      copyReferenceId: variable.copyReferenceId,
      stateId: stateName ?? '',
    }
  })
}

export function pairOldAndNewAssumptions({
  oldAssumptions,
  newAssumptions,
  oldVariables,
  newVariables,
  oldVariableStates,
  newVariableStates,
}: {
  oldAssumptions: Array<Assumption>
  newAssumptions: Array<Assumption>
  oldVariables: Array<Variable>
  newVariables: Array<Variable>
  oldVariableStates: Array<VariableStateSelectEntry>
  newVariableStates: Array<VariableStateSelectEntry>
}) {
  const oldAssumptionsWithCopyReferenceId = addVariablePropsToAssumption(
    oldAssumptions,
    oldVariables,
    oldVariableStates,
  )
  const newAssumptionsWithCopyReferenceId = addVariablePropsToAssumption(
    newAssumptions,
    newVariables,
    newVariableStates,
  )
  const pairedAssumptions = pairNewAndOldEntities({
    oldEntities: oldAssumptionsWithCopyReferenceId,
    newEntities: newAssumptionsWithCopyReferenceId,
    findEntityInListOfEntity: findAssumptionInAssumptions,
  })
  return pairedAssumptions.map(({ newEntity, oldEntity }) => ({
    oldAssumption: oldEntity,
    newAssumption: newEntity,
  }))
}

function findAssumptionInAssumptions(
  assumptions: Array<
    Assumption & { copyReferenceId: string; variableStates: string[] }
  >,
  assumption: Assumption & {
    copyReferenceId: string
    variableStates: string[]
  },
) {
  return assumptions.find((oldAssumption) => {
    if (oldAssumption.copyReferenceId === assumption.copyReferenceId) {
      return true
    }
    return oldAssumption.variableName === assumption.variableName
  })
}

function addVariablePropsToAssumption(
  assumptions: Array<Assumption>,
  variables: Array<Variable>,
  variableStates: Array<VariableStateSelectEntry>,
) {
  return assumptions.map((assumption) => {
    const variable = variables.find((v) => v.id === assumption.variableId)
    if (!variable) throw new Error('Variable not found for assumption')
    return {
      ...assumption,
      copyReferenceId: variable.copyReferenceId,
      variableStates: variable.stateIds.map((stateId) => {
        const stateWithMetadata = variableStates.find(
          (state) => state.id === stateId,
        )
        if (!stateWithMetadata) {
          throw new Error(
            `State not found for variable: ${variable.name} (stateId: ${stateId})`,
          )
        }
        return stateWithMetadata.name
      }),
    }
  })
}

export function pairOldAndNewAssumptionSets({
  oldAssumptionSets,
  newAssumptionSets,
}: {
  oldAssumptionSets: Array<AssumptionSetAggregate>
  newAssumptionSets: Array<AssumptionSetAggregate>
}) {
  const pairedAssumptionSets = pairNewAndOldEntities({
    oldEntities: oldAssumptionSets,
    newEntities: newAssumptionSets,
    findEntityInListOfEntity: findAssumptionSetInAssumptionSets,
  })
  return pairedAssumptionSets.map(({ newEntity, oldEntity }) => ({
    oldAssumptionSet: oldEntity,
    newAssumptionSet: newEntity,
  }))
}

function findAssumptionSetInAssumptionSets(
  assumptionSets: Array<AssumptionSetAggregate>,
  assumptionSet: AssumptionSetAggregate,
) {
  return assumptionSets.find((oldAssumptionSet) => {
    if (oldAssumptionSet.copyReferenceId === assumptionSet.copyReferenceId) {
      return true
    }
    return oldAssumptionSet.name === assumptionSet.name
  })
}

function pairNewAndOldEntities<T>({
  oldEntities,
  newEntities,
  findEntityInListOfEntity,
}: {
  oldEntities: Array<T>
  newEntities: Array<T>
  findEntityInListOfEntity: (entities: Array<T>, entity: T) => T | undefined
}) {
  const allEntitiesInBothNetworks = [
    ...oldEntities,
    ...newEntities.flatMap((entity) => {
      const exists = findEntityInListOfEntity(oldEntities, entity)
      if (exists) {
        return []
      }
      return [entity]
    }),
  ]

  return allEntitiesInBothNetworks.map((entity) => {
    const oldEntity = findEntityInListOfEntity(oldEntities, entity)
    const newEntity = findEntityInListOfEntity(newEntities, entity)
    return {
      newEntity,
      oldEntity,
    }
  })
}

/**
 * Resolve an assumption's observed state name from its `stateIndex`
 * into the variable's state list. `stateIndex === undefined` means the
 * assumption was created without an explicit observation, in which
 * case the conventional label is `"unobserved"`.
 *
 * Lives next to the assumption-pairing primitives because it operates
 * on the `variableStates` array attached to assumptions by
 * `pairOldAndNewAssumptions`.
 */
export function getObservedState(
  stateIndex: number | undefined,
  variableStates: string[],
): string {
  return stateIndex !== undefined ? variableStates[stateIndex] : 'unobserved'
}
